import { useState } from "react";
import { HttpError, useCustomMutation, useGetIdentity, useInvalidate, useList, useTranslation, CanAccess } from "@refinedev/core";
import { calendar_v3 } from "googleapis";
import dayjs from "dayjs";
import { roundTime } from '@utils/time';
import { useAppCalendar } from "hooks/calendar/useAppCalendar";
import { EmergencyTemplate } from "@components/templates/emergency";
import HelpProfessionalCard, { HelpProfessionalCardProps } from "@components/molecules/HelpProfessionalCard";
import { HELPS_CHOOSE_PROFESSIONAL_REFRESH_INTERVAL, HELPS_EMERGENCY_TIME_MINUTES } from "core/helps/definitions"
import { Auth_Users, Availability } from "@graphql/schema.types";
import { CREATE_EVENT_QUERY } from "./queries";
import { CreateEventMutation } from "@graphql/types";
import { FaRotateRight, FaTriangleExclamation, FaPhone, FaArrowLeft } from "react-icons/fa6";
import { EventCode, getMetadataString, APP_NAME } from "core/calendar/definitions";
import { PlansPopup } from "@components/molecules/PlansPopup";
import { ServiceOrPlanChoice } from "@components/molecules/ServiceOrPlanChoice";
import { useAtomValue } from "jotai";
import { roleAtom } from "../../auth/firebase/state";
import { useServicePurchase } from "../../hooks/useServicePurchase";

export const ContactProfessional = () => {
    const { data: helps, isLoading: isLoadingAvailability, isFetching: isFetchingAvailability, refetch, ...rest } = useList<Availability>({
        resource: "availability",
        sorters: [
            {
                field: 'updated_at',
                order: 'desc',
            },
        ],
        filters: [
            {
                field: "type",
                operator: "eq",
                value: "helps",
            },
            {
                field: "status",
                operator: "eq",
                value: "available",
            },
        ],
        pagination: {
            current: 1,
            pageSize: 10,
        },
        queryOptions: {
            refetchInterval: HELPS_CHOOSE_PROFESSIONAL_REFRESH_INTERVAL
        },
        meta: {
            fields: [
                'id',
                'status',
                'type',
                'updated_at',
                {
                    'user_professional_public': [
                        'display_name',
                        'avatar_url',
                        'email',
                        'id',
                        {
                            'professional': [
                                'specialty',
                                'description',
                                {
                                    'professional_specialties': [
                                        'name'
                                    ]
                                },
                                'country',
                                {
                                    'country_info': [
                                        'name'
                                    ],
                                },
                            ]
                        },
                    ]
                }
            ]
        }
    });

    const calendarId = useAppCalendar()
    const { data: user } = useGetIdentity<Auth_Users>()
    const { mutateAsync: createEvent, isLoading: isLoadingCreateEvent } = useCustomMutation<CreateEventMutation, HttpError, any>()
    const invalidate = useInvalidate()
    const { translate } = useTranslation()

    async function createMeetAndRedirect(profesional: { id: string, name?: string, email?: string }) {
        if (!calendarId) return

        const { data: eventRes } = await createEvent({
            url: '',
            method: 'post',
            values: {},
            meta: {
                gqlMutation: CREATE_EVENT_QUERY,
                gqlVariables: {
                    professional: {
                        id: profesional.id,
                        email: profesional.email
                    },
                    event: {
                        summary: `${translate(`calendar-defs.events.${EventCode.EMERGENCY_CALL}`)} - ${APP_NAME}`,
                        description: getMetadataString({
                            code: EventCode.EMERGENCY_CALL
                        }),
                        conferenceData: {
                            createRequest: {
                                requestId: crypto.randomUUID(),
                                conferenceSolutionKey: {
                                    type: 'hangoutsMeet'
                                }
                            },
                        },
                        start: {
                            dateTime: roundTime(dayjs()).toISOString(),
                            timeZone: 'America/Bogota'
                        },
                        end: {
                            dateTime: roundTime(dayjs()).add(HELPS_EMERGENCY_TIME_MINUTES, 'minutes').toISOString(),
                            timeZone: 'America/Bogota'
                        },
                        source: {
                            title: 'hachimind emergency',
                            url: 'https://users.hachimind.com'
                        },
                        attendees: [
                            {
                                email: user?.email,
                                displayName: user?.display_name
                            }
                        ],
                    } as calendar_v3.Schema$Event
                }
            }
        })

        await invalidate({
            dataProviderName: 'gapi',
            resource: 'calendar/calendars/:calendar/events',
            invalidates: ['list']
        })

        const meetUrl = eventRes?.create_event?.event.meet.url
        if (!!meetUrl)
            window.open(meetUrl, '_blank');
    }

    const [active, setActive] = useState(-1)

    const professionals = (helps?.data?.map((help) => {
        const userProfessional = help?.user_professional_public

        return userProfessional ? {
            id: userProfessional.id,
            name: userProfessional.display_name,
            email: userProfessional.email,
            description: userProfessional.professional?.description,
            photo: userProfessional.avatar_url,
            specialty: [userProfessional.professional?.specialty, ...(userProfessional.professional?.professional_specialties?.map(s => s.name) || [])],
            country: {
                code: userProfessional.professional?.country,
                name: userProfessional.professional?.country_info?.name,
            }
        } : null
    }).filter(help => !!help) ?? []) as HelpProfessionalCardProps[];

    const { role } = useAtomValue(roleAtom);
    const [showService, setShowService] = useState(false);

    // Get help feature ID
    const { data: features } = useList({
        resource: "features",
        filters: [{ field: "name", operator: "eq", value: "help" }],
        pagination: { current: 1, pageSize: 1 },
        meta: { fields: ["id", "name"] }
    });
    
    const helpFeatureId = features?.data?.[0]?.id;
    const { hasPurchased, isLoading: purchaseLoading } = useServicePurchase(helpFeatureId);

    // Para user_band0, verificar si ya compró el servicio
    if (role === 'user_band0') {
        if (purchaseLoading) {
            return <EmergencyTemplate>
                <div className="flex items-center justify-center min-h-[400px]">
                    <div className="loading loading-spinner loading-lg"></div>
                </div>
            </EmergencyTemplate>;
        }
        
        if (!hasPurchased && !showService) {
            return <EmergencyTemplate>
                <ServiceOrPlanChoice 
                    onUseService={() => setShowService(true)}
                    serviceTitle="Comprar servicio"
                    serviceDescription="Accede directamente a profesionales disponibles para emergencias"
                    serviceIcon={<FaPhone />}
                    serviceButtonText="Continuar con la compra"
                    serviceType="help"
                />
            </EmergencyTemplate>;
        }
    }

    return <EmergencyTemplate>
        <CanAccess 
            resource="contact-professional" 
            action="show" 
            params={{ validateSubscription: true }}
            fallback={<PlansPopup />}
        >
            <div className="flex flex-col xl:items-center w-full xl:min-h-full p-4">
            {/* Header Section */}
            <div className="w-full max-w-2xl mb-6">
                <div className="text-center mb-6">
                    <h1 className="text-2xl md:text-3xl font-bold text-base-content mb-2">
                        {translate("page.helps.options.contactProfessional.title")}
                    </h1>
                    <p className="text-base-content/70 text-sm md:text-base">
                        {translate("page.helps.options.contactProfessional.description")}
                    </p>
                </div>

                {/* Status Indicator */}
                <div className="flex items-center justify-center gap-2 mb-4">
                    <div className={`w-3 h-3 rounded-full ${isFetchingAvailability ? 'bg-warning animate-pulse' : 'bg-success'}`}></div>
                    <span className="text-sm text-base-content/70">
                        {isFetchingAvailability
                            ? translate("common.loading")
                            : `${professionals.length} ${translate("page.helps.professionalsAvailable")}`
                        }
                    </span>
                </div>
            </div>

            {/* Content Section */}
            <div className="w-full max-w-2xl">
                {professionals.length > 0 ? (
                    <div className="space-y-4">
                        {professionals.map((prof, i) =>
                            <HelpProfessionalCard
                                key={i}
                                {...prof}
                                active={active == i}
                                onContact={() => createMeetAndRedirect(prof)}
                                onClick={() => setActive(i)}
                                contactActive={!isLoadingCreateEvent && !!calendarId}
                            />
                        )}
                    </div>
                ) : (
                    <div className="card bg-base-100 shadow-lg border border-base-300">
                        <div className="card-body p-6 text-center">
                            <div className="flex justify-center mb-4">
                                <div className={`p-4 rounded-full ${isFetchingAvailability ? 'bg-warning/20' : 'bg-error/20'}`}>
                                    <FaTriangleExclamation
                                        className={`w-8 h-8 ${isFetchingAvailability ? 'text-warning animate-spin' : 'text-error'}`}
                                    />
                                </div>
                            </div>

                            <h2 className="text-xl font-semibold mb-2">
                                {translate("page.helps.no-professionals-card.title")}
                            </h2>

                            <p className="text-base-content/70 mb-6">
                                {translate("page.helps.no-professionals-card.description")}
                            </p>

                            <div className="flex flex-col sm:flex-row gap-3 justify-center">
                                <button
                                    className="btn btn-primary gap-2"
                                    onClick={() => !isFetchingAvailability && refetch()}
                                    disabled={isFetchingAvailability}
                                >
                                    <FaRotateRight className={`w-4 h-4 ${isFetchingAvailability ? 'animate-spin' : ''}`} />
                                    {isFetchingAvailability ? translate("common.loading") : translate("common.reload")}
                                </button>

                                <button
                                    className="btn btn-outline gap-2"
                                    onClick={() => window.history.back()}
                                >
                                    <FaArrowLeft className="w-4 h-4" />
                                    {translate("common.go-back")}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Loading State */}
            {isLoadingAvailability && (
                <div className="w-full max-w-2xl mt-8">
                    <div className="card bg-base-100 shadow-lg">
                        <div className="card-body p-6 text-center">
                            <div className="loading loading-spinner loading-lg mb-4"></div>
                            <p className="text-base-content/70">
                                {translate("page.helps.searchingProfessionals")}
                            </p>
                        </div>
                    </div>
                </div>
            )}
            </div>
        </CanAccess>
    </EmergencyTemplate>;
}
